import React, { useState, useEffect, useCallback, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useCookies } from 'react-cookie';
import {
    Box, Typography, CircularProgress, Alert, Button, TextField,
    Paper, LinearProgress, Chip, Table, TableBody, TableRow, TableCell, TableHead,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import Barcode from 'react-barcode';
import { useReactToPrint } from 'react-to-print';
import NotificationSettings from '../NotificationSettings';

const ClaimsRelabel = ({ posConfig }) => {
    const [cookies] = useCookies(['secret', 'station', 'staff']);
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [nextToken, setNextToken] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);

    // Active relabel process
    const [activeClaim, setActiveClaim] = useState(null);
    const [relabelDetails, setRelabelDetails] = useState(null);
    const [relabelLoading, setRelabelLoading] = useState(false);
    const [scanValue, setScanValue] = useState('');
    const [relabelStatus, setRelabelStatus] = useState(null); // { type, msg }
    const [label, setLabel] = useState(null);

    // Top search bar
    const [searchValue, setSearchValue] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);

    const scanInputRef = useRef(null);
    const searchInputRef = useRef(null);
    const labelRef = useRef(null);
    const notifHandlerRef = useRef(null);

    const socketUrl = posConfig?.socketUrl || process.env.REACT_APP_WEBSOCKET_URL;

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        queryParams: { station: cookies.station, secret: cookies.secret, staff: cookies.staff },
        onMessage: (message) => handleWsEvent(message),
        shouldReconnect: () => true,
        retryOnError: true,
    });

    const handlePrint = useReactToPrint({ content: () => labelRef.current });

    const handleWsEvent = useCallback((message) => {
        try {
            const msg = JSON.parse(message.data);

            // Forward notification settings messages to widget
            if (msg.task === 'respGetStationNotificationSettings' || msg.task === 'respUpdateStationNotificationSettings') {
                if (typeof notifHandlerRef.current === 'function') notifHandlerRef.current(msg);
                return;
            }

            // Handle incoming station notifications (e.g. from another station approving a claim)
            if (msg.task === 'stationNotification') {
                setSearchStatus({ type: 'info', msg: `${msg.title}: ${msg.message}` });
                // Reload the list
                loadClaims();
                return;
            }

            if (msg.task === 'respGetApprovedClaimsForRelabel') {
                if (msg.status === 'succeeded') {
                    if (loadingMore) {
                        setClaims(prev => [...prev, ...(msg.claims || [])]);
                    } else {
                        setClaims(msg.claims || []);
                    }
                    setNextToken(msg.nextToken || null);
                    setError(null);
                } else {
                    setError(msg.message || 'Fehler beim Laden.');
                }
                setLoading(false);
                setLoadingMore(false);
            }

            if (msg.task === 'respGetClaimRelabelDetails') {
                setRelabelLoading(false);
                if (msg.status === 'succeeded') {
                    setRelabelDetails(msg);
                } else {
                    setRelabelStatus({ type: 'error', msg: msg.message || 'Fehler.' });
                }
            }

            if (msg.task === 'respSearchClaimByMpsid') {
                setSearchLoading(false);
                if (msg.status === 'found') {
                    if (msg.alreadyDone) {
                        setSearchStatus({ type: 'info', msg: 'Bereits umetikettiert – Label wird erneut gedruckt.' });
                    } else {
                        setSearchStatus({ type: 'success', msg: 'Gefunden! Vorgang wird geöffnet...' });
                    }
                    const found = claims.find(c => c.claimId === msg.claimId);
                    if (found) {
                        openRelabelProcess(found);
                    } else {
                        openRelabelProcessById(msg.claimId, msg.mpsid);
                    }
                } else if (msg.status === 'not_found') {
                    setSearchStatus({ type: 'warning', msg: 'MPSID nicht in der Relabel-Liste enthalten.' });
                } else {
                    setSearchStatus({ type: 'error', msg: msg.message || 'Fehler.' });
                }
            }

            if (msg.task === 'respPrintRelabel') {
                if (msg.status === 'succeeded') {
                    setLabel(msg.label);
                    setRelabelStatus({ type: 'success', msg: 'Label wird gedruckt...' });
                    // Remove from list
                    setClaims(prev => prev.filter(c => c.claimId !== msg.claimId));
                } else {
                    setRelabelStatus({ type: 'error', msg: msg.message || 'Fehler beim Drucken.' });
                }
            }
        } catch (e) {
            console.error('WS parse error:', e);
        }
    }, [claims, loadingMore]);

    // Print label when it arrives
    useEffect(() => {
        if (label) {
            setTimeout(() => {
                handlePrint();
                // Reset after printing
                setTimeout(() => {
                    setActiveClaim(null);
                    setRelabelDetails(null);
                    setLabel(null);
                    setRelabelStatus(null);
                    setScanValue('');
                    setSearchValue('');
                    setSearchStatus(null);
                    // Refocus search input
                    searchInputRef.current?.focus();
                }, 500);
            }, 300);
        }
    }, [label]);

    const loadClaims = useCallback((token) => {
        if (token) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        sendMessage(JSON.stringify({
            task: 'getApprovedClaimsForRelabel',
            station: cookies.station,
            staff: cookies.staff,
            nextToken: token || undefined,
        }));
    }, [sendMessage, cookies.station, cookies.staff]);

    useEffect(() => {
        if (readyState === ReadyState.OPEN) loadClaims();
    }, [readyState, loadClaims]);

    const openRelabelProcess = (claim) => {
        setActiveClaim(claim);
        setRelabelDetails(null);
        setRelabelLoading(true);
        setRelabelStatus(null);
        setLabel(null);
        setScanValue('');
        sendMessage(JSON.stringify({
            task: 'getClaimRelabelDetails',
            station: cookies.station,
            staff: cookies.staff,
            mpsid: claim.zugeordneteSendung,
        }));
    };

    const openRelabelProcessById = (claimId, mpsid) => {
        setActiveClaim({ claimId, zugeordneteSendung: mpsid });
        setRelabelDetails(null);
        setRelabelLoading(true);
        setRelabelStatus(null);
        setLabel(null);
        setScanValue('');
        sendMessage(JSON.stringify({
            task: 'getClaimRelabelDetails',
            station: cookies.station,
            staff: cookies.staff,
            mpsid,
        }));
    };

    // Focus scan input when details loaded
    useEffect(() => {
        if (relabelDetails) {
            setTimeout(() => scanInputRef.current?.focus(), 100);
        }
    }, [relabelDetails]);

    const handleScanInput = (val) => {
        if (!activeClaim || !relabelDetails) return;
        const cleaned = val.replace(/^x/, '').replace(/x$/, '');

        if (cleaned === relabelDetails.mpsid || cleaned === activeClaim.zugeordneteSendung) {
            // Match! Print relabel
            setRelabelStatus({ type: 'info', msg: 'MPSID stimmt überein. Label wird gedruckt...' });
            sendMessage(JSON.stringify({
                task: 'printRelabel',
                station: cookies.station,
                staff: cookies.staff,
                claimId: activeClaim.claimId,
                mpsid: activeClaim.zugeordneteSendung,
            }));
        } else {
            setRelabelStatus({ type: 'error', msg: `Falsche MPSID. Erwartet: ${relabelDetails.mpsid}, Gescannt: ${cleaned}` });
            setScanValue('');
            scanInputRef.current?.focus();
        }
    };

    const handleSearchScan = (val) => {
        const cleaned = val.replace(/^x/, '').replace(/x$/, '');
        if (!cleaned) return;
        setSearchLoading(true);
        setSearchStatus(null);
        sendMessage(JSON.stringify({
            task: 'searchClaimByMpsid',
            station: cookies.station,
            staff: cookies.staff,
            mpsid: cleaned,
        }));
    };

    const closeRelabelProcess = () => {
        setActiveClaim(null);
        setRelabelDetails(null);
        setRelabelStatus(null);
        setLabel(null);
        setScanValue('');
        searchInputRef.current?.focus();
    };

    const fmtDate = (iso) => {
        if (!iso) return '–';
        const d = new Date(iso);
        return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    return (
        <Box sx={{ minHeight: '100vh' }}>
            <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2.5, py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Neu zugeordnete Sendungen Umetikettieren (Relabel)
                    </Typography>
                    <NotificationSettings
                        sendMessage={sendMessage}
                        cookies={cookies}
                        registerMessageHandler={(h) => { notifHandlerRef.current = h; }}
                        unregisterMessageHandler={() => { notifHandlerRef.current = null; }}
                    />
                </Box>

                {/* ── Top Search Bar ── */}
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8f7f4', border: '1px solid #e5e5e3', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                        MPSID scannen zum Suchen
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                            inputRef={searchInputRef}
                            fullWidth
                            size="small"
                            placeholder="MPSID-Barcode scannen..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.charCode === 13 && searchValue) {
                                    handleSearchScan(searchValue);
                                    setSearchValue('');
                                }
                            }}
                            autoComplete="off"
                            autoFocus={!activeClaim}
                            disabled={!!activeClaim}
                        />
                        {searchLoading && <CircularProgress size={20} />}
                    </Box>
                    {searchStatus && (
                        <Alert severity={searchStatus.type} sx={{ mt: 1, borderRadius: 2, py: 0 }}>
                            {searchStatus.msg}
                        </Alert>
                    )}
                </Paper>

                {/* ── Active Relabel Process ── */}
                {activeClaim && (
                    <Paper sx={{ p: 3, mb: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                                Relabel-Vorgang: {activeClaim.zugeordneteSendung}
                            </Typography>
                            <Button size="small" variant="outlined" color="error" onClick={closeRelabelProcess}>
                                Abbrechen
                            </Button>
                        </Box>

                        {relabelLoading && <LinearProgress sx={{ mb: 2 }} />}

                        {relabelDetails && (
                            <Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#999' }}>Lagerplatz</Typography>
                                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1976d2' }}>
                                            {relabelDetails.lagerplatzText || '–'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#999' }}>Sendungsnummer</Typography>
                                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                            {relabelDetails.sendungsnummer}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#999' }}>MPSID</Typography>
                                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                            {relabelDetails.mpsid}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#999' }}>Kdnr</Typography>
                                        <Typography>{relabelDetails.kdnr}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#999' }}>Spedition</Typography>
                                        <Typography>{relabelDetails.spedition}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#999' }}>Absender</Typography>
                                        <Typography>{relabelDetails.absender}</Typography>
                                    </Box>
                                </Box>

                                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                                    Jetzt MPSID scannen zur Bestätigung:
                                </Typography>
                                <TextField
                                    inputRef={scanInputRef}
                                    fullWidth
                                    size="small"
                                    placeholder="MPSID-Barcode scannen..."
                                    value={scanValue}
                                    onChange={(e) => setScanValue(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.charCode === 13 && scanValue) {
                                            handleScanInput(scanValue);
                                        }
                                    }}
                                    autoComplete="off"
                                    autoFocus
                                    onMouseEnter={() => scanInputRef.current?.focus()}
                                />
                            </Box>
                        )}

                        {relabelStatus && (
                            <Alert severity={relabelStatus.type} sx={{ mt: 2, borderRadius: 2 }}>
                                {relabelStatus.msg}
                            </Alert>
                        )}
                    </Paper>
                )}

                {/* ── Claims List ── */}
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : claims.length === 0 ? (
                    <Typography sx={{ py: 6, textAlign: 'center', color: '#999', fontSize: 14 }}>
                        Keine freigegebenen Claims zum Umetikettieren.
                    </Typography>
                ) : (
                    <>
                        <Table size="small" sx={{ bgcolor: '#fff', borderRadius: 2, overflow: 'hidden', border: '1px solid #e5e5e3' }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8f7f4' }}>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>MPSID</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Kdnr</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Kunde</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Sendungsnummer</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Absender</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Freigegeben am</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {claims.map((claim) => (
                                    <TableRow
                                        key={claim.claimId}
                                        hover
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: activeClaim?.claimId === claim.claimId ? '#e3f2fd' : 'inherit',
                                            '&:hover': { bgcolor: '#f0f0ee' },
                                        }}
                                        onClick={() => openRelabelProcess(claim)}
                                    >
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                                            {claim.zugeordneteSendung}
                                        </TableCell>
                                        <TableCell>{claim.kdnr}</TableCell>
                                        <TableCell>{claim.kundenName}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                                            {claim.sendungsnummer}
                                        </TableCell>
                                        <TableCell>{claim.absender}</TableCell>
                                        <TableCell>{fmtDate(claim.geaendertAm)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={<PrintIcon sx={{ fontSize: 14 }} />}
                                                label="Relabel"
                                                size="small"
                                                sx={{ fontSize: 11, fontWeight: 600 }}
                                                clickable
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {nextToken && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => loadClaims(nextToken)}
                                    disabled={loadingMore}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600 }}
                                >
                                    {loadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                    Weitere laden
                                </Button>
                            </Box>
                        )}
                    </>
                )}

                {/* ── Hidden Label for Printing ── */}
                {label && (
                    <Box sx={{ display: 'none' }}>
                        <Box ref={labelRef}>
                            <Box sx={{ pl: 1 }}>
                                <Barcode value={'x' + label.mpsid + 'x'} displayValue={false} height="50mm" width={2.25} />
                                <Box px={1}>
                                    <Typography sx={{ lineHeight: 1.1, fontSize: '16px', fontWeight: 'bold' }}>
                                        MPS-ID: <b>{label.mpsid}</b>
                                    </Typography>
                                    <Typography sx={{ lineHeight: 1.1, fontSize: '24px', fontWeight: 'bold' }}>
                                        {label.kdnr}
                                    </Typography>
                                    <Typography sx={{ lineHeight: 1.1, fontSize: '32px', fontWeight: 'bold' }}>
                                        {(label.lastname + '').toUpperCase()}
                                    </Typography>
                                    <Typography sx={{ lineHeight: 1.1, fontSize: '20px', fontWeight: 'bold' }}>
                                        {label.firstname}
                                    </Typography>
                                    <Typography sx={{ lineHeight: 1.1, fontSize: '20px', fontWeight: 'bold' }}>
                                        <b>{label.klasse}</b>
                                    </Typography>
                                    <Typography sx={{ lineHeight: 1.1, fontSize: '16px', fontWeight: 'bold' }}>
                                        {label.comment}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ClaimsRelabel;
